<!--
  StorefrontPasswordPage -- the shared-password wall for a protected store.

  The router guard (router/storefrontPasswordGate.js) redirects here for any
  `/shop/:slug/*` page of a password-protected store the visitor has not yet
  cleared. This page is a LEAF: it renders the password form and nothing about
  the store's products, cart or orders, so there is no content to leak behind
  it.

  ON A CORRECT PASSWORD the page records the per-store "cleared" marker and
  forwards the visitor to where they were originally headed. It does NOT log
  them in.

  Why not: `validate_store_password` (api/src/endpoints/campaign.py) accepts
  only `{password, campaign_id}`. It has no user identifier, so it cannot
  establish WHO the visitor is -- only that they know the store's shared
  secret. The previous flow responded to a correct password by swapping the
  visitor onto the store's shared shopper account, which handed every visitor
  of a store the SAME identity, and with it that account's order history and
  saved addresses. Jake's decision: the password proves "you are allowed into
  this store", not "who you are". The visitor stays anonymous with a working
  cart; identity is collected at checkout.

  THE SESSION COMES FIRST (card nrNUCjgU, 2026-10-08). Before the form, the
  page establishes the visitor's own per-visitor storefront session through
  `ensureStorefrontSession`, the same step StorefrontSessionGate runs. The
  password check carries that session's bearer, and a correct password makes
  the API record a server-side GATE PASS for it: the API refuses a password
  store's content, and payroll orders, without one. This is not the old
  shared-account swap -- the session is the visitor's own anonymous one, so
  the storefront chrome still withholds the personal Profile / Orders /
  Addresses / Logout links.

  The two markers are deliberately SEPARATE flags answering different
  questions -- "may this browser see this store?" vs "is this session riding
  the store's shared account?" -- and must not be overloaded onto each other.
  See router/anonymousStorefrontSession.js.

  Behavioural tests: `__tests__/storefront-password-page.spec.js`.
-->
<template>
    <div class="storefront-password-page" data-testid="storefront-password-wall">
        <div
            v-if="state === 'loading'"
            class="wall-loading"
            data-testid="password-wall-loading"
        >
            Loading store. Please wait...
        </div>

        <div
            v-else-if="state === 'invalid'"
            class="wall-invalid"
            data-testid="password-wall-invalid"
        >
            <h2>Store not found</h2>
            <p>Please make sure your url is correct.</p>
        </div>

        <storefront-sign-in
            v-else
            :campaignID="campaignID"
            :brandColor="brandColor"
            :signInInfo="signInInfo"
            @sign-in="onPasswordAccepted"
        />
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import StorefrontSignIn from "@/components/Storefront/StorefrontSignIn";
import { markStoreGateCleared } from "@/router/storefrontPasswordGate";
import { ensureStorefrontSession } from "@/router/ensureStorefrontSession";

export default {
    name: "StorefrontPasswordPage",

    components: {
        "storefront-sign-in": StorefrontSignIn,
    },

    data() {
        return {
            // 'loading' | 'invalid' | 'ready'
            state: "loading",
            campaignID: null,
            signInInfo: null,
        };
    },

    computed: {
        slug() {
            const value = this.$route?.params?.slug;
            return typeof value === "string" && value.length ? value : null;
        },

        brandColor() {
            const color = this.signInInfo?.brand_color;
            // White-on-white sign-in button is invisible; match the
            // StorefrontSessionGate fallback.
            if (!color || color === "#FFFFFF") return "#000000";
            return color;
        },
    },

    async mounted() {
        await this.load();
    },

    methods: {
        async load() {
            this.state = "loading";
            if (!this.slug) {
                this.state = "invalid";
                return;
            }

            try {
                const idResp = await this.$axios.get(
                    `/campaign/getCampaignIdFromShopperURL/${this.slug}`
                );
                this.campaignID = idResp?.data ?? null;
                if (!this.campaignID) {
                    this.state = "invalid";
                    return;
                }

                const infoResp = await this.$axios.post(
                    `/campaign/getCampaignSignInInfo`,
                    { campaign_id: this.campaignID }
                );
                this.signInInfo = infoResp?.data ?? null;

                // The visitor's OWN storefront session, established before
                // the form (card nrNUCjgU). A correct password records a
                // server-side gate pass for this session, which the API
                // requires before it serves the store or takes a payroll
                // order; the check carries its bearer. The same step the
                // store's session gate runs, so the session it finds or
                // mints here is the one the store then keeps.
                const session = await ensureStorefrontSession({
                    slug: this.slug,
                    axiosInstance: this.$axios,
                    store: this.$store,
                });
                if (session?.status !== "ready") {
                    this.state = "invalid";
                    return;
                }
                this.state = "ready";
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Storefront password wall failed to load", describeError(err));
                this.state = "invalid";
            }
        },

        /**
         * The shared password was accepted (StorefrontSignIn only emits
         * `sign-in` after the backend answered "authenticated").
         *
         * Record the cleared fact and forward. Deliberately NO token swap onto
         * the shared shopper account, and no identity keys written here: the
         * visitor keeps the per-visitor session `load()` established, which
         * the server has now given its gate pass -- see the file header.
         */
        onPasswordAccepted() {
            markStoreGateCleared(this.slug);
            this.$router.replace(this.destination());
        },

        /**
         * Where to send the visitor after they clear the wall.
         *
         * Only an in-STORE path is honoured. An absolute URL, a
         * protocol-relative `//host`, or a path belonging to a DIFFERENT store
         * all fall back to this store's home -- otherwise the `redirect` query
         * param would be an open-redirect (and a cross-store one at that: the
         * marker just written is for THIS store, so forwarding into another
         * store would land the visitor on that store's wall anyway).
         */
        destination() {
            const home = `/shop/${this.slug}`;
            const requested = this.$route?.query?.redirect;
            if (typeof requested !== "string" || !requested) return home;

            // Must be a same-origin absolute PATH: exactly one leading slash.
            // `//evil.example.com` is protocol-relative and would leave the
            // site, so a second leading slash disqualifies it.
            if (!requested.startsWith("/") || requested.startsWith("//")) {
                return home;
            }

            // Must be inside THIS store's tree.
            if (requested !== home && !requested.startsWith(`${home}/`)) {
                return home;
            }

            return requested;
        },
    },
};
</script>

<style scoped>
.wall-loading,
.wall-invalid {
    padding: 64px 24px;
    text-align: center;
    color: #444;
}

.wall-invalid h2 {
    margin: 0 0 8px 0;
    font-size: 22px;
}

.wall-invalid p {
    margin: 0;
    color: #777;
}
</style>
